import * as XLSX from 'xlsx';

export const normalize = (str: string | undefined | null) => {
    if (!str) return '';
    return str.toString().trim().toUpperCase().replace(/\s+/g, '');
};

export const isIgnored = (row: any) => {
    const notesValue =
        `${row['Notes'] || ''} ${row['Notes Value'] || ''} ${row['NOTE'] || ''} ${row['Note'] || ''}`.toLowerCase();
    const statusValue = normalize(row['Status']);

    return (
        // NOTES FILTER
        notesValue.includes('ignore') ||
        notesValue.includes('cancel') ||
        notesValue.includes('duplicate') ||
        // STATUS FILTER
        statusValue.includes('UPDATEEMAIL') ||
        statusValue.includes('UPDATEWORKINGDRAWING') ||
        statusValue.includes('REVISIONDWG') ||
        statusValue.includes('UPDATEDENIAL') ||
        statusValue.includes('UPDATEFIELDING') ||
        statusValue.includes('UPDATEPOWERMAP')
    );
};

export const parseExcelFile = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const json = XLSX.utils.sheet_to_json(worksheet);
                resolve(json);
            } catch (error) {
                reject(error);
            }
        };
        reader.onerror = (error) => reject(error);
        reader.readAsArrayBuffer(file);
    });
};

const generateKey = (row: any) => {
    if (row['KEY']) return String(row['KEY']).trim();

    const jobNum = String(row['Job Number'] || '')
        .replace(/[\s-]/g, '')
        .toUpperCase();
    const idNum = String(row['ID Number'] || '')
        .replace(/[\s-]/g, '')
        .toUpperCase();
    const otherNum = String(row['Other Number'] || '')
        .replace(/[\s-]/g, '')
        .toUpperCase();

    return `${jobNum}_${idNum}_${otherNum}`;
};

export const compareData = (reportData: any[], gdevData: any[]) => {
    const filteredReportData = reportData
        .filter((row) => !isIgnored(row))
        .map((row) => ({ ...row, KEY: generateKey(row) }));
    const filteredGdevData = gdevData
        .filter((row) => !isIgnored(row))
        .map((row) => ({ ...row, KEY: generateKey(row) }));

    const reportKeys = new Set(
        filteredReportData.map((row) => String(row['KEY']).trim()),
    );
    const gdevKeys = new Set(
        filteredGdevData.map((row) => String(row['KEY']).trim()),
    );

    const missingInGDEV = filteredReportData
        .filter((row) => !gdevKeys.has(String(row['KEY']).trim()))
        .map((row) => ({ ...row, Status: 'Missing in GDEV' }));
    const missingInReportForm = filteredGdevData
        .filter((row) => !reportKeys.has(String(row['KEY']).trim()))
        .map((row) => ({ ...row, Status: 'Missing in Report Form' }));

    // Find matched keys
    const reportKeysArr = filteredReportData.map((row) =>
        String(row['KEY']).trim(),
    );
    const matchedKeys = new Set(reportKeysArr.filter((x) => gdevKeys.has(x)));

    // Simplistic logic for matched MRE vs OCALC:
    // If ID Number contains MRE -> MRE
    // Otherwise we'll just put them in OCALC for now, or match it against some column.
    let matchedMRE = 0;
    let matchedOCALC = 0;
    let mismatchCount = 0;
    const mismatchedReportRows: any[] = [];
    const mismatchedGdevRows: any[] = [];

    matchedKeys.forEach((key) => {
        const rRow = filteredReportData.find(
            (r) => String(r['KEY']).trim() === key,
        );
        const gRow = filteredGdevData.find(
            (r) => String(r['KEY']).trim() === key,
        );

        if (rRow && gRow) {
            const reportPole = String(rRow['Total Pole'] || '0').trim();
            const gdevPole = String(gRow['Pole Complete'] || '0').trim();

            if (reportPole !== gdevPole) {
                mismatchCount++;
                mismatchedReportRows.push({
                    ...rRow,
                    Status: 'Mismatch: Pole Count',
                });
                mismatchedGdevRows.push({
                    ...gRow,
                    Status: 'Mismatch: Pole Count',
                });
            }

            const idNumber = String(rRow['ID Number'] || '').toUpperCase();
            if (idNumber.includes('MRE')) {
                matchedMRE++;
            } else {
                matchedOCALC++;
            }
        }
    });

    return {
        totalReport: reportData.length,
        totalGDEV: gdevData.length,
        filteredReportCount: filteredReportData.length,
        missing: missingInGDEV.length,
        mismatch: mismatchCount,
        matchedMRE,
        matchedOCALC,
        reportTableData: [...missingInGDEV, ...mismatchedReportRows],
        gdevTableData: [...missingInReportForm, ...mismatchedGdevRows],
    };
};
